import React from 'react';
import type {
	CustomizableTabZoneBlockParams,
	DashboardBlockParams,
	StaticAppParams,
	TileZoneBlockParams,
} from '@sharefiledev/projects-pilet-sdk';
import type { ExtensionComponentProps, PiletApi } from '@sharefiledev/sharefile-appshell';
import type { BlockParams } from '@sharefiledev/solution-view-engine';
import { Skeleton } from 'antd';
import { BlockNames } from '../block.types';
import { LoadingSpinner } from '../LoadingSpinner';

const LazyStaticApp = React.lazy(() => import('./StaticApp'));
export const AsyncStaticApp: React.FC<
	ExtensionComponentProps<BlockParams & StaticAppParams>
> = ({ piral, params }) => (
	<React.Suspense fallback={<LoadingSpinner />}>
		<LazyStaticApp piletApi={piral} {...params} />
	</React.Suspense>
);

const LazyFilesPanel = React.lazy(() => import('./FilesPanel'));
export const AsyncFilesPanel: React.FC<
	ExtensionComponentProps<CustomizableTabZoneBlockParams & BlockParams>
> = ({ piral, params }) => (
	<React.Suspense fallback={<LoadingSpinner />}>
		<LazyFilesPanel piletApi={piral} {...params} />
	</React.Suspense>
);

const LazyDashboardBlock = React.lazy(() => import('./DashboardBlock'));
export const AsyncDashboardBlock: React.FC<
	ExtensionComponentProps<DashboardBlockParams & BlockParams>
> = ({ piral, params }) => (
	<React.Suspense fallback={<LoadingSpinner />}>
		<LazyDashboardBlock piletApi={piral} {...params} />
	</React.Suspense>
);

export function addBlocks(piral: PiletApi) {
	const LazyHeaderAndCustomizableTabs = React.lazy(
		() => import('./HeaderAndCustomizableTabs')
	);
	piral.registerExtension(BlockNames.Headerwithcustomizabletabs, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyHeaderAndCustomizableTabs {...props} />
		</React.Suspense>
	));

	const LazyHeaderAndPanel = React.lazy(() => import('./HeaderAndPanel'));
	piral.registerExtension(BlockNames.Headerwithpanel, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyHeaderAndPanel {...props} />
		</React.Suspense>
	));

	const LazyAssociatedProjectsDropdownHeader = React.lazy(
		() => import('./AssociatedProjectsDropdownHeader')
	);
	piral.registerExtension(BlockNames.AssociatedProjectsDropdownHeader, props => (
		<React.Suspense fallback={<Skeleton />}>
			<LazyAssociatedProjectsDropdownHeader {...props} />
		</React.Suspense>
	));

	const LazyErrorStateBlock = React.lazy(() => import('./ErrorStateBlock'));
	piral.registerExtension(BlockNames.HeaderwithcustomizabletabsErrorState, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyErrorStateBlock {...props} />
		</React.Suspense>
	));

	const LazyProjectLifeCycleStageAwareBlock = React.lazy(
		() => import('./ProjectLifeCycleStageAwareBlock')
	);
	piral.registerExtension(BlockNames.ProjectLifeCycleStageAwareView, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectLifeCycleStageAwareBlock {...props} />
		</React.Suspense>
	));

	const LazyProjectListView = React.lazy(() => import('./ProjectListBlock'));
	piral.registerExtension(BlockNames.ProjectListView, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectListView {...props} />
		</React.Suspense>
	));

	const LazyTabEmptyStateBlock = React.lazy(() => import('./TabEmptyStateBlock'));
	piral.registerExtension(BlockNames.ProjectListViewTabEmptyState, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyTabEmptyStateBlock {...props} />
		</React.Suspense>
	));

	const LazyProjectsViewByTags = React.lazy(() => import('./ProjectsViewByTagsBlock'));
	piral.registerExtension(BlockNames.ProjectsViewByTags, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectsViewByTags {...props} />
		</React.Suspense>
	));

	const LazySuggestedProjectsByUsers = React.lazy(
		() => import('./SuggestedProjectsBlock')
	);
	piral.registerExtension(BlockNames.SuggestedProjects, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazySuggestedProjectsByUsers {...props} />
		</React.Suspense>
	));

	const LazyProjectHeaderTourBlock = React.lazy(() => import('./ProjectHeaderTourBlock'));
	piral.registerExtension(BlockNames.ProjectHeaderTour, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectHeaderTourBlock {...props} />
		</React.Suspense>
	));

	const LazyProjectContainerMoreOptionsDropdown = React.lazy(
		() => import('./ProjectContainerMoreOptionsDropdown')
	);
	piral.registerExtension(BlockNames.TitleInlineMetaOptionsDropdown, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectContainerMoreOptionsDropdown {...props} />
		</React.Suspense>
	));

	const LazyRecentlyDeletedBlock = React.lazy(() => import('./RecentlyDeletedBlock'));
	piral.registerExtension(BlockNames.RecentlyDeleted, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyRecentlyDeletedBlock {...props} />
		</React.Suspense>
	));

	const LazyRecentlyDeletedTitleMetadata = React.lazy(
		() => import('./RecentlyDeletedTitleMetadata')
	);
	piral.registerExtension(BlockNames.RecentlyDeletedTitleMetadata, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyRecentlyDeletedTitleMetadata {...props} />
		</React.Suspense>
	));

	const LazyProjectLoader = React.lazy(() => import('./ProjectLoaderBlock'));
	piral.registerExtension(BlockNames.ProjectLoader, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectLoader {...props} />
		</React.Suspense>
	));

	const LazyCreateProjectMenuButton = React.lazy(
		() => import('./CreateProjectMenuButtonBlock')
	);
	piral.registerExtension(BlockNames.CreateProjectMenuButton, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyCreateProjectMenuButton {...props} />
		</React.Suspense>
	));

	const LazyNewProjectOptions = React.lazy(() => import('./NewProjectOptionsBlock'));
	piral.registerExtension(BlockNames.NewProjectOptions, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyNewProjectOptions {...props} />
		</React.Suspense>
	));

	const LazyProjectProviderBlock = React.lazy(() => import('./ProjectProviderBlock'));
	piral.registerExtension(BlockNames.Provider, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyProjectProviderBlock {...props} />
		</React.Suspense>
	));

	const LazyFTUEmptyStateBlock = React.lazy(() => import('./FTUEmptyStateBlock'));
	piral.registerExtension(BlockNames.ProjectListViewFtuState, props => (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazyFTUEmptyStateBlock {...props} />
		</React.Suspense>
	));
}

const LazyFilesTileBlock = React.lazy(() => import('./FilesTileBlock'));
export const AsyncFilesTileBlock: React.FC<
	ExtensionComponentProps<TileZoneBlockParams & BlockParams>
> = ({ piral, params }) => (
	<React.Suspense fallback={<LoadingSpinner />}>
		<LazyFilesTileBlock piletApi={piral} {...params} />
	</React.Suspense>
);
